import React from 'react';
import { Chip } from '@jotpsych/ui';
import {
  alpha,
  Autocomplete,
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { LooseEndDecision, LooseEndSet } from '../types';
import {
  defaultQuickWinDecision,
  isMultiProviderSet,
  isStaleSet,
} from '../utils/quickWins';
import {
  flagTitle,
  formatLastUsed,
  groupArchiveSuggestion,
  lastUsedLabel,
  namesSpanVisitCategories,
} from '../utils/versionSignals';

type SetAction = 'merge' | 'archive' | 'skip';

/**
 * Compact one-line summary of the reconciled (merged) setup. Every adopted
 * value names its source when not every member carries it — "60 min (from
 * 'Intake')" — so the admin can judge whose setup wins before merging.
 */
const reconciledSummary = (set: LooseEndSet): string => {
  const r = set.near?.reconciled;
  if (!r) return '';
  const withSource = (
    value: string | null,
    has: (d: LooseEndSet['entries'][number]['version']['defaults']) => boolean
  ): string | null => {
    if (!value) return null;
    const holders = set.entries.filter((e) => has(e.version.defaults));
    if (holders.length === 0 || holders.length === set.entries.length) {
      return value;
    }
    return `${value} (from "${holders[0].name}")`;
  };
  const parts = [
    withSource(r.durationLabel, (d) => d.durationLabel !== null),
    withSource(r.modalityLabel, (d) => d.modalityLabel !== null),
    withSource(
      r.placeOfServiceLabel ?? r.placeOfServiceCode,
      (d) => (d.placeOfServiceLabel ?? d.placeOfServiceCode) !== null
    ),
    withSource(
      r.cptCodes.length ? `CPT ${r.cptCodes.join(', ')}` : null,
      (d) => d.cptCodes.length > 0
    ),
    withSource(
      r.forms.length ? `${r.forms.length} forms` : null,
      (d) => d.forms.length > 0
    ),
    withSource(
      r.reminders.length ? `${r.reminders.length} reminders` : null,
      (d) => d.reminders.length > 0
    ),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'no extra defaults';
};

const actionOf = (decision: LooseEndDecision): SetAction =>
  decision.archive ? 'archive' : decision.merge ? 'merge' : 'skip';

export interface StepLooseEndsProps {
  sets: LooseEndSet[];
  decisions: Readonly<Record<string, LooseEndDecision>>;
  onDecisionChange: (key: string, decision: LooseEndDecision) => void;
  /** Copy overrides — the quick-wins step reuses this screen. */
  heading?: string;
  lede?: React.ReactNode;
  emptyHeading?: string;
  emptyBody?: string;
  disabled?: boolean;
}

const SetCard: React.FC<{
  set: LooseEndSet;
  decision: LooseEndDecision;
  onDecisionChange: (key: string, decision: LooseEndDecision) => void;
  disabled: boolean;
}> = ({ set, decision, onDecisionChange, disabled }) => {
  const names = [...new Set(set.entries.map((e) => e.name))];
  // Same template + defaults makes these mechanically interchangeable, but if
  // the NAMES span visit categories (an intake grouped with a follow-up) the
  // "interchangeable" claim reads as wrong to a clinician — soften it and warn.
  const spansVisitCategories = namesSpanVisitCategories(
    set.entries.map((e) => e.name)
  );
  const multiProvider = isMultiProviderSet(set);
  const templateFlag = flagTitle(set.templateTitle);
  const keepTrimmed = decision.keepName.trim();
  const isCustomName = keepTrimmed !== '' && !names.includes(keepTrimmed);
  const customNameFlag = isCustomName ? flagTitle(keepTrimmed) : null;
  // Duplicate names exist WITHIN a set — highlight one keeper row only.
  // entries are usage-sorted, so the first match is the most-used one.
  const keeperId = set.entries.find((e) => e.name === keepTrimmed)?.version.id;
  const action = actionOf(decision);
  const stale = isStaleSet(set);
  const staleSince = stale
    ? groupArchiveSuggestion(set.entries.map((e) => e.version))?.newestUse
    : null;
  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${
          action === 'merge'
            ? alpha(theme.palette.success.main, 0.4)
            : action === 'archive'
              ? alpha(theme.palette.warning.main, 0.45)
              : theme.palette.divider
        }`,
        borderRadius: 1.5,
        bgcolor:
          action === 'merge'
            ? alpha(theme.palette.success.main, 0.04)
            : action === 'archive'
              ? alpha(theme.palette.warning.main, 0.04)
              : theme.palette.background.paper,
        p: 1.25,
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      })}
    >
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {set.entries.length}
            {spansVisitCategories
              ? ' types on one template'
              : ' interchangeable types'}
          </Typography>
          {spansVisitCategories && (
            <Chip
              size="xSmall"
              variant="soft"
              color="warning"
              label="names span intake & follow-up — confirm these are the same visit"
            />
          )}
          {set.templateTitle && (
            <Chip
              size="xSmall"
              variant="soft"
              color="success"
              label={`template: ${set.templateTitle}`}
            />
          )}
          {templateFlag && (
            <Chip
              size="xSmall"
              variant="soft"
              color="error"
              label={`template ${templateFlag}`}
            />
          )}
          {set.near && (
            <Chip
              size="xSmall"
              variant="soft"
              color="info"
              label={
                set.near.namesOverlap
                  ? 'near match · names overlap'
                  : 'near match · compatible setups'
              }
            />
          )}
          {multiProvider ? (
            <Chip
              size="xSmall"
              variant="soft"
              color="info"
              label={`${new Set(set.entries.map((e) => e.version.provider ?? 'practice')).size} providers agree`}
            />
          ) : (
            <Chip
              size="xSmall"
              variant="soft"
              color="warning"
              label="one provider's labels — check before merging"
            />
          )}
          {stale && (
            <Chip
              size="xSmall"
              variant="soft"
              color="warning"
              label={`no use since ${staleSince ? formatLastUsed(staleSince) : 'ever'} — archive suggested`}
            />
          )}
          {action === 'merge' && isCustomName && (
            <Chip
              size="xSmall"
              variant="soft"
              color="primary"
              label={`all ${set.entries.length} renamed to "${keepTrimmed}"`}
            />
          )}
          {action === 'merge' && customNameFlag && (
            <Chip
              size="xSmall"
              variant="soft"
              color="error"
              label={`new name ${customNameFlag}`}
            />
          )}
          {action === 'archive' && (
            <Chip
              size="xSmall"
              variant="soft"
              color="warning"
              label={`all ${set.entries.length} will archive`}
            />
          )}
        </Stack>
        <Box sx={{ mt: 0.5 }}>
          {set.entries.map(({ version, name }) => {
            const isKeeper = action === 'merge' && version.id === keeperId;
            return (
              <Typography
                key={version.id}
                variant="caption"
                component="div"
                sx={{
                  color: isKeeper ? 'primary.dark' : 'text.secondary',
                  fontWeight: isKeeper ? 600 : 400,
                  lineHeight: 1.7,
                }}
              >
                {isKeeper ? '● ' : '○ '}
                {name} · {version.usage} visits
                {version.lastUsedAt && ` · ${lastUsedLabel(version.lastUsedAt)}`}
                {version.provider && ` · ${version.provider}`}
                {isKeeper && '  ← survives'}
              </Typography>
            );
          })}
          {set.near && (
            <Typography
              variant="caption"
              component="div"
              sx={{
                mt: 0.5,
                color: action === 'merge' ? 'success.dark' : 'text.disabled',
                fontWeight: action === 'merge' ? 600 : 400,
              }}
            >
              → merged setup: {reconciledSummary(set)} · template stays{' '}
              {set.templateTitle ?? 'unset'}
            </Typography>
          )}
        </Box>
      </Box>
      <Stack spacing={2} sx={{ flexShrink: 0, width: 230 }}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={action}
          disabled={disabled}
          onChange={(_, next: SetAction | null) => {
            if (!next) return;
            onDecisionChange(set.key, {
              ...decision,
              merge: next === 'merge',
              archive: next === 'archive' || undefined,
            });
          }}
        >
          <ToggleButton value="merge">Merge</ToggleButton>
          <ToggleButton value="archive">Archive</ToggleButton>
          <ToggleButton value="skip">Skip</ToggleButton>
        </ToggleButtonGroup>
        {action === 'merge' && (
          <Autocomplete
            freeSolo
            disableClearable
            size="small"
            options={names}
            // The field is pre-filled with the suggestion, and default
            // filtering would narrow the dropdown to just that — always
            // show every candidate name (the lists are tiny).
            filterOptions={(options) => options}
            inputValue={decision.keepName}
            disabled={disabled}
            onInputChange={(_, value) =>
              onDecisionChange(set.key, { ...decision, keepName: value })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Keep a name — or type one"
                helperText={
                  keepTrimmed === ''
                    ? "Empty — we'll fall back to the suggested name"
                    : isCustomName
                      ? `New name — all ${set.entries.length} types renamed`
                      : undefined
                }
              />
            )}
          />
        )}
      </Stack>
    </Box>
  );
};

/**
 * Shared set-consolidation screen (GDN-2226): quick wins up front and the
 * loose-ends sweep at the end both resolve interchangeable-type sets —
 * same template, identical defaults, only the name differs. Multi-provider
 * sets come pre-checked ("providers agree"); single-provider sets are
 * opt-in under their own subheader. Pure presentation.
 */
export const StepLooseEnds: React.FC<StepLooseEndsProps> = ({
  sets,
  decisions,
  onDecisionChange,
  heading = 'A few shared types ended up interchangeable',
  lede = (
    <>
      After your merges, these types point at the <b>same template with
      identical defaults</b> — only the names differ. Merging them is safe
      (nothing about the visit changes); pick which name survives — or type
      a better one.
    </>
  ),
  emptyHeading = 'No loose ends 🎉',
  emptyBody = 'After your merges, no two shared types are interchangeable — ' +
    'every type either has its own template or its own defaults.',
  disabled = false,
}) => {
  if (sets.length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {emptyHeading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          {emptyBody}
        </Typography>
      </Box>
    );
  }

  const nearMatches = sets.filter((s) => s.near);
  const exact = sets.filter((s) => !s.near);
  const agreed = exact.filter(isMultiProviderSet);
  const optIn = exact.filter((s) => !isMultiProviderSet(s));
  const decisionOf = (set: LooseEndSet): LooseEndDecision =>
    decisions[set.key] ?? defaultQuickWinDecision(set);
  const mergingCount = sets.filter(
    (s) => actionOf(decisionOf(s)) === 'merge'
  ).length;
  const archivingCount = sets.filter(
    (s) => actionOf(decisionOf(s)) === 'archive'
  ).length;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {heading}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 680 }}
      >
        {lede}
      </Typography>

      {agreed.length > 0 && (
        <Stack spacing={1} sx={{ mb: optIn.length > 0 ? 2.5 : 0 }}>
          {agreed.map((set) => (
            <SetCard
              key={set.key}
              set={set}
              decision={decisionOf(set)}
              onDecisionChange={onDecisionChange}
              disabled={disabled}
            />
          ))}
        </Stack>
      )}

      {optIn.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5, mt: agreed.length > 0 ? 0 : 0 }}
          >
            Opt-in — one provider&apos;s own labels
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, maxWidth: 680 }}
          >
            These match mechanically, but a single provider made every copy —
            the different names may be deliberate. Check the ones you&apos;re
            sure about.
          </Typography>
          <Stack spacing={1}>
            {optIn.map((set) => (
              <SetCard
                key={set.key}
                set={set}
                decision={decisionOf(set)}
                onDecisionChange={onDecisionChange}
                disabled={disabled}
              />
            ))}
          </Stack>
        </>
      )}

      {nearMatches.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5, mt: 2.5 }}
          >
            Near matches — same template, compatible setups
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, maxWidth: 680 }}
          >
            These share a template and their defaults don&apos;t conflict —
            where one sets a value and the other doesn&apos;t, the merge
            adopts it (shown per card, with where it came from). If adopting
            a value feels wrong, that&apos;s your sign they&apos;re genuinely
            different visit types — skip. Nothing here is pre-checked.
          </Typography>
          <Stack spacing={1}>
            {nearMatches.map((set) => (
              <SetCard
                key={set.key}
                set={set}
                decision={decisionOf(set)}
                onDecisionChange={onDecisionChange}
                disabled={disabled}
              />
            ))}
          </Stack>
        </>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {mergingCount} of {sets.length} sets will merge
        {archivingCount > 0 && ` · ${archivingCount} will archive (stale)`} —
        nothing applies until you confirm.
      </Typography>
    </Box>
  );
};
